import { ArrowDown, ArrowUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CustomToolStep } from '@/types/custom-tool.types';
import { useCustomToolBuilder } from '../builder/builder-context';
import { dataSourcesOf, withAfter, withStepMoved } from '../custom-tool-draft';
import { StepBadges } from '../steps/step-badges';
import { stepFields, useShownFields } from '../steps/step-fields';
import { StepMenu } from '../steps/step-menu';
import { useStepName } from '../steps/step-name';
import { StepTitle } from '../steps/step-title';
import { ArgumentRow } from './argument-row';
import { BLOCK, FOCUSED } from './block';

const CHIP = 'flex items-center gap-1 rounded-full border px-2 leading-5';

/** The steps this one waits for: the ones it takes data from, then the ones it is set to run after. */
const RunsAfter = ({ step }: { step: CustomToolStep }) => {
	const { draft, change } = useCustomToolBuilder();
	const stepName = useStepName();
	const dataFrom = dataSourcesOf(step);
	const after = step.after.filter((id) => !dataFrom.includes(id));
	const name = (id: string) => {
		const other = draft.steps.find((candidate) => candidate.id === id);
		return other ? stepName(other) : id;
	};

	if (!dataFrom.length && !after.length) {
		return <p className='text-[11px] leading-4 text-gray-800'>Waits for no other step, so it starts right away.</p>;
	}
	return (
		<div className='flex flex-wrap items-center gap-1.5 text-[11px] leading-4 text-gray-800'>
			Runs after
			{dataFrom.map((id) => (
				<span key={id} className={cn(CHIP, 'border-violet-200 bg-violet-50 text-violet-800')}>{`${name(id)} (takes its data)`}</span>
			))}
			{after.map((id) => (
				<span key={id} className={cn(CHIP, 'border-gray-500 bg-gray-50 pr-0.5 text-gray-900')}>
					{name(id)}
					<button
						type='button'
						aria-label={`Stop waiting for ${name(id)}`}
						className='rounded-full p-0.5 hover:bg-gray-300'
						onClick={() => change((current) => withAfter(current, step.id, step.after.filter((other) => other !== id)))}
					>
						<X className='size-3' />
					</button>
				</span>
			))}
		</div>
	);
};

/** One step as a block: its tool, where each argument comes from, and what it waits for. */
export const StepBlock = ({ step, index }: { step: CustomToolStep; index: number }) => {
	const { draft, change, focus, stepTools, fieldsOf } = useCustomToolBuilder();
	const tool = stepTools.toolFor(step);
	const toolFields = fieldsOf(step) ?? [];
	const fields = stepFields(step, toolFields);
	const { shown, unset, showOptional, toggleOptional } = useShownFields(step, fields);
	const missing = toolFields.some((field) => field.required && !step.args[field.name]);
	const focused = focus?.kind === 'step' && focus.id === step.id;
	const move = (offset: -1 | 1) => change((current) => withStepMoved(current, step.id, offset));

	return (
		<article
			data-block={`step-${step.id}`}
			aria-label={`Step ${index + 1}`}
			className={cn(BLOCK, focused ? FOCUSED : missing ? 'border-red-300' : 'border-gray-300')}
		>
			<header className='flex items-center gap-2'>
				<StepTitle
					step={step}
					index={index}
					tool={tool}
					detail={
						<>
							{' · '}
							<span className='font-mono'>{step.id}</span>
						</>
					}
				/>
				<StepBadges step={step} tool={tool} />
				<Button variant='ghost' size='icon-sm' aria-label={`Move step ${index + 1} up`} disabled={index === 0} onClick={() => move(-1)}>
					<ArrowUp className='size-3.5' />
				</Button>
				<Button
					variant='ghost'
					size='icon-sm'
					aria-label={`Move step ${index + 1} down`}
					disabled={index === draft.steps.length - 1}
					onClick={() => move(1)}
				>
					<ArrowDown className='size-3.5' />
				</Button>
				<StepMenu step={step} index={index} />
			</header>
			{fields.length === 0 ? (
				<p className='text-xs leading-4 text-gray-800'>{stepTools.isLoading ? 'Loading' : 'Takes no arguments'}</p>
			) : (
				<div className='flex flex-col gap-2'>
					{shown.map((field) => (
						<ArgumentRow key={field.name} step={step} field={field} />
					))}
					{unset > 0 && (
						<button
							type='button'
							aria-expanded={showOptional}
							onClick={toggleOptional}
							className='self-start text-xs leading-4 text-gray-800 hover:text-ink'
						>
							{showOptional ? 'Hide optional' : `+ ${unset} optional`}
						</button>
					)}
				</div>
			)}
			<div className='border-t border-gray-300 pt-3'>
				<RunsAfter step={step} />
			</div>
		</article>
	);
};
